/**
 * Payment step of the cart — summary of the orders being checked out, payment
 * method picker, and the Charge / Mark as collected CTA.
 *
 * The cart section creates the invoice before handing over, so the amount shown
 * is what Stripe actually still owes after account credit. This section owns
 * the charge mutation and its error states.
 */
import { ArrowLeft, CreditCard, Loader2, ShoppingCart, TriangleAlert } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import type { OrderWithOwnerStripeUrl } from '@snootsvet/shared'
import {
  formatClinicPrice,
  matchesPredicate,
  ORDER_PREDICATES,
  REGIONS_BY_ID,
} from '@snootsvet/shared'

import { Alert, AlertDescription, AlertTitle } from '@/frontend/components/ui/alert'
import { Button } from '@/frontend/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/frontend/components/ui/table'
import { TapToPayPanel } from '@/frontend/features/orders/checkout-screen/tap-to-pay-panel'
import { DeclinedPaymentPanel } from '@/frontend/features/orders/declined-payment-panel'
import {
  type CheckoutInvoice,
  useHandleCheckout,
} from '@/frontend/features/orders/hooks/use-handle-checkout'
import { OrderItemCell } from '@/frontend/features/orders/order-item-cell'
import {
  PaymentMethodPicker,
  TAP_TO_PAY_METHOD,
} from '@/frontend/features/orders/payment-method-picker'

export interface CheckoutPaymentSectionProps {
  /** Orders selected for checkout — non-empty. */
  orders: OrderWithOwnerStripeUrl[]
  /** Invoice created for the chargeable orders; null when every order is free. */
  invoice: CheckoutInvoice | null
  /** Return to the cart, whether or not payment was taken. The invoice stays open until the
   * member's next checkout replaces it. */
  onBack: () => void
  /** Whether a mutation is in progress elsewhere — disables the CTA. */
  mutating: boolean
  onMutatingChange: (mutating: boolean) => void
  /** Reports whether a tap-to-pay invoice is open and waiting for the webhook to settle it. */
  onAwaitingPaymentChange: (awaiting: boolean) => void
}

export function CheckoutPaymentSection({
  orders,
  invoice,
  onBack,
  mutating,
  onMutatingChange,
  onAwaitingPaymentChange,
}: CheckoutPaymentSectionProps) {
  // Orders are charged together in a single currency, so amounts render in the
  // clinic's currency (what's actually billed), not the viewer's region.
  const clinicId = orders[0].clinicId
  const owner = orders[0].patient.owner
  const hasStripeAccount = !!owner.stripeCustomerId

  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null)
  const tapToPay = paymentMethodId === TAP_TO_PAY_METHOD

  const total = useMemo(
    () =>
      orders
        .filter((o) => matchesPredicate(o, ORDER_PREDICATES.invoiceable))
        .reduce((sum, o) => sum + o.totalPrice, 0),
    [orders]
  )
  const amountDue = invoice?.amountRemaining ?? 0
  const creditApplied = invoice ? total - amountDue : 0
  const needsPayment = amountDue > 0

  const checkout = useHandleCheckout(orders, {
    invoice,
    onSuccess: (charged) => {
      toast.success(
        charged > 0 ? `Charged ${formatClinicPrice(charged, clinicId)}` : 'Marked as collected'
      )
      onBack()
    },
  })
  const { checkout: handleCheckout, checkoutError, isSubmitting } = checkout

  const canConfirm =
    (!needsPayment || (!!paymentMethodId && !tapToPay && hasStripeAccount)) &&
    !isSubmitting &&
    !mutating

  const handleConfirm = async () => {
    if (!canConfirm) return
    onMutatingChange(true)
    try {
      await handleCheckout(
        orders.map((o) => o.id),
        owner.id,
        paymentMethodId
      )
    } finally {
      onMutatingChange(false)
    }
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-headline-sm text-muted-foreground">
          <ShoppingCart className="h-4 w-4" />
          Checkout
        </h3>
        <Button variant="ghost" onClick={onBack} disabled={isSubmitting}>
          <ArrowLeft className="h-4 w-4" />
          Back to cart
        </Button>
      </div>

      <Table size="sm" variant="borderless" className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead>Item</TableHead>
            <TableHead className="w-14 text-center">Qty</TableHead>
            <TableHead className="w-16 text-right">Discount</TableHead>
            <TableHead className="w-24 text-right">Price</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((order) => (
            <TableRow key={order.id}>
              <OrderItemCell order={order} />
              <TableCell className="text-center">{order.quantity}</TableCell>
              <TableCell className="text-right">
                {order.discountBps > 0 ? `${order.discountBps / 100}%` : '-'}
              </TableCell>
              <TableCell className="text-right">
                {matchesPredicate(order, ORDER_PREDICATES.invoiceable)
                  ? formatClinicPrice(order.totalPrice, order.clinicId)
                  : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter className="bg-inherit">
          {creditApplied > 0 && (
            <>
              <TableRow>
                <TableCell colSpan={3} className="text-right text-muted-foreground">
                  Total
                </TableCell>
                <TableCell className="text-right text-muted-foreground">
                  {formatClinicPrice(total, clinicId)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={3} className="text-right text-muted-foreground">
                  Account credit
                </TableCell>
                <TableCell className="text-right text-muted-foreground">
                  −{formatClinicPrice(creditApplied, clinicId)}
                </TableCell>
              </TableRow>
            </>
          )}
          <TableRow>
            <TableCell colSpan={3} className="text-right font-bold">
              {creditApplied > 0 ? 'To pay' : 'Total'}
            </TableCell>
            <TableCell className="text-right font-bold">
              {formatClinicPrice(amountDue, clinicId)}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>

      {needsPayment && !hasStripeAccount && (
        <Alert variant="warning" className="mt-4">
          <TriangleAlert />
          <AlertTitle>No billing account</AlertTitle>
          <AlertDescription>This member doesn't have a Stripe account linked.</AlertDescription>
        </Alert>
      )}

      {needsPayment && hasStripeAccount ? (
        <div className="mt-4">
          <h3 className="mb-2 flex items-center gap-2 text-headline-sm text-muted-foreground">
            <CreditCard className="h-4 w-4" />
            Payment method
          </h3>
          <PaymentMethodPicker
            memberId={owner.id}
            value={paymentMethodId}
            onChange={setPaymentMethodId}
            allowTapToPay={REGIONS_BY_ID[owner.region].tapToPay}
          />
          {tapToPay ? (
            <div className="mt-4">
              <TapToPayPanel
                amountDue={amountDue}
                total={total}
                clinicId={clinicId}
                owner={owner}
                customerEmail={invoice?.customerEmail ?? null}
                onAwaitingPaymentChange={onAwaitingPaymentChange}
              />
            </div>
          ) : (
            <>
              {checkoutError?.kind === 'invoice-failed' && (
                <Alert variant="destructive" className="mt-4">
                  <AlertTitle>Couldn't create invoice</AlertTitle>
                  <AlertDescription>
                    {checkoutError.message ??
                      'Something went wrong creating the invoice. Please try again or contact support.'}
                  </AlertDescription>
                </Alert>
              )}
              {checkoutError?.kind === 'charge-failed' && (
                <Alert variant="destructive" className="mt-4">
                  <AlertTitle>Payment failed</AlertTitle>
                  <AlertDescription>
                    {checkoutError.message ??
                      'Something went wrong processing the payment. Please try again or contact support.'}
                  </AlertDescription>
                </Alert>
              )}
              <DeclinedPaymentPanel checkout={checkout} />
              <div className="mt-4 flex justify-end">
                <Button disabled={!canConfirm} onClick={handleConfirm}>
                  {isSubmitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CreditCard className="h-4 w-4" />
                  )}
                  Charge {formatClinicPrice(amountDue, clinicId)}
                </Button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="mt-4 flex justify-end">
          <Button disabled={!canConfirm} onClick={handleConfirm}>
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ShoppingCart className="h-4 w-4" />
            )}
            Mark as collected
          </Button>
        </div>
      )}
    </div>
  )
}
